import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  CloudDownload as PullIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  RestartAlt as ResetIcon,
  YouTube as YouTubeIcon,
  Sync as SyncIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

function formatDuration(seconds) {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function TrainingYoutubeLibrary({ categories = [] }) {
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [channelDetail, setChannelDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [subTab, setSubTab] = useState(0);

  const [addOpen, setAddOpen] = useState(false);
  const [channelUrl, setChannelUrl] = useState('');

  const [groupOpen, setGroupOpen] = useState(false);
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    training_category_id: '',
  });
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [assignGroupId, setAssignGroupId] = useState(null);
  const [assignVideoIds, setAssignVideoIds] = useState([]);

  const [config, setConfig] = useState(null);

  const loadChannels = useCallback(async () => {
    const r = await api.get('/training/admin/youtube-library/channels');
    setChannels(r.data.data || []);
  }, []);

  const loadGroups = useCallback(async () => {
    const r = await api.get('/training/admin/youtube-library/groups');
    setGroups(r.data.data || []);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes] = await Promise.all([
        api.get('/training/youtube-library/config'),
        loadChannels(),
        loadGroups(),
      ]);
      setConfig(configRes.data.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load YouTube library');
    } finally {
      setLoading(false);
    }
  }, [loadChannels, loadGroups]);

  const loadChannelDetail = useCallback(async (id) => {
    if (!id) {
      setChannelDetail(null);
      return;
    }
    setDetailLoading(true);
    try {
      const r = await api.get(`/training/admin/youtube-library/channels/${id}`);
      setChannelDetail(r.data.data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load channel videos');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    loadChannelDetail(selectedChannelId);
  }, [selectedChannelId, loadChannelDetail]);

  const selectedChannel = useMemo(
    () => channels.find((c) => c.id === selectedChannelId) || channelDetail,
    [channels, selectedChannelId, channelDetail]
  );

  const handleAddChannel = async () => {
    if (!channelUrl.trim()) {
      toast.error('Enter a YouTube channel URL');
      return;
    }
    try {
      const r = await api.post('/training/admin/youtube-library/channels', { input_url: channelUrl.trim() });
      toast.success('Channel added');
      setAddOpen(false);
      setChannelUrl('');
      await loadChannels();
      setSelectedChannelId(r.data.data.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to add channel');
    }
  };

  const handleInitialPull = async () => {
    if (!selectedChannelId) return;
    setPulling(true);
    try {
      const r = await api.post(`/training/admin/youtube-library/channels/${selectedChannelId}/pull`);
      const d = r.data.data || {};
      const modeNote = d.note ? ` ${d.note}` : '';
      toast.success(`Pulled ${d.added} videos (metadata only, all private).${modeNote}`);
      await loadChannels();
      await loadChannelDetail(selectedChannelId);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Pull failed');
    } finally {
      setPulling(false);
    }
  };

  const handleIncrementalPull = async () => {
    if (!selectedChannelId) return;
    setPulling(true);
    try {
      const r = await api.post(`/training/admin/youtube-library/channels/${selectedChannelId}/pull-incremental`);
      toast.success(`Added ${r.data.data.added} new video(s)`);
      await loadChannels();
      await loadChannelDetail(selectedChannelId);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Incremental pull failed');
    } finally {
      setPulling(false);
    }
  };

  const handleResetChannel = async () => {
    if (!selectedChannelId) return;
    if (!window.confirm('Remove all pulled videos for this channel? You can pull again cleanly afterward.')) return;
    setPulling(true);
    try {
      await api.post(`/training/admin/youtube-library/channels/${selectedChannelId}/reset`);
      toast.success('Channel library cleared');
      await loadChannels();
      await loadChannelDetail(selectedChannelId);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Reset failed');
    } finally {
      setPulling(false);
    }
  };

  const handleRefreshNewCount = async () => {
    if (!selectedChannelId) return;
    try {
      await loadChannels();
      await loadChannelDetail(selectedChannelId);
    } catch {
      /* loadChannelDetail toasts */
    }
  };

  const toggleVisibility = async (videoId, visibility) => {
    try {
      await api.patch(`/training/admin/youtube-library/videos/${videoId}/visibility`, { visibility });
      await loadChannelDetail(selectedChannelId);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update visibility');
    }
  };

  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) {
      toast.error('Group name is required');
      return;
    }
    try {
      const payload = {
        name: groupForm.name.trim(),
        description: groupForm.description || null,
        training_category_id: groupForm.training_category_id || null,
      };
      if (editingGroupId) {
        await api.put(`/training/admin/youtube-library/groups/${editingGroupId}`, payload);
        toast.success('Group updated');
      } else {
        await api.post('/training/admin/youtube-library/groups', payload);
        toast.success('Group created');
      }
      setGroupOpen(false);
      setEditingGroupId(null);
      setGroupForm({ name: '', description: '', training_category_id: '' });
      await loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save group');
    }
  };

  const openAssignVideos = (group) => {
    setAssignGroupId(group.id);
    setAssignVideoIds((group.videos || []).map((v) => v.id));
  };

  const handleSaveAssignVideos = async () => {
    try {
      await api.put(`/training/admin/youtube-library/groups/${assignGroupId}/videos`, {
        video_ids: assignVideoIds,
      });
      toast.success('Videos assigned to group');
      setAssignGroupId(null);
      await loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to assign videos');
    }
  };

  const handleSyncTraining = async (groupId) => {
    try {
      const r = await api.post(`/training/admin/youtube-library/groups/${groupId}/sync-training`);
      toast.success(`Synced ${r.data.data.created} new + ${r.data.data.updated} updated materials`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Sync failed');
    }
  };

  const allVideos = channelDetail?.videos || [];
  const hasStoredVideos = (selectedChannel?.video_count_stored || 0) > 0;
  const newCount = selectedChannel?.new_videos_available;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity={config?.api_key_configured ? 'info' : 'warning'} sx={{ mb: 2, borderRadius: 2 }}>
        <Typography variant="body2">
          {config?.message || (
            <>
              <strong>YouTube Library</strong> stores links and metadata only — videos are never downloaded or uploaded.
              Public channels can be indexed without an API key via YouTube&apos;s RSS feed (~15 recent videos). Set{' '}
              <code>YOUTUBE_API_KEY</code> on the backend for full channel history, duration, and view counts.
            </>
          )}
        </Typography>
      </Alert>

      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
        <Tab label="Channels & videos" />
        <Tab label="Course groups" />
      </Tabs>

      {subTab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Channels
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} variant="contained" onClick={() => setAddOpen(true)}>
                    Add
                  </Button>
                </Stack>
                {channels.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Add a channel such as https://www.youtube.com/@freecodecamp
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {channels.map((ch) => (
                      <Button
                        key={ch.id}
                        variant={selectedChannelId === ch.id ? 'contained' : 'outlined'}
                        onClick={() => setSelectedChannelId(ch.id)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.25 }}
                      >
                        <Stack alignItems="flex-start" spacing={0.25} sx={{ width: '100%' }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <YouTubeIcon sx={{ fontSize: 18 }} />
                            <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'left' }}>
                              {ch.title || ch.handle || ch.youtube_channel_id}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="inherit" sx={{ opacity: 0.85 }}>
                            {ch.video_count_stored || 0} stored
                            {ch.new_videos_available != null && ch.new_videos_available > 0
                              ? ` · ${ch.new_videos_available} new on YouTube`
                              : ''}
                          </Typography>
                        </Stack>
                      </Button>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {!selectedChannelId ? (
              <Card variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
                <Typography color="text.secondary">Select a channel to manage pulls and videos.</Typography>
              </Card>
            ) : (
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                    {!hasStoredVideos ? (
                      <Button
                        variant="contained"
                        startIcon={pulling ? <CircularProgress size={18} color="inherit" /> : <PullIcon />}
                        disabled={pulling}
                        onClick={handleInitialPull}
                      >
                        YouTube library pull (one time)
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          startIcon={pulling ? <CircularProgress size={18} color="inherit" /> : <PullIcon />}
                          disabled={pulling || newCount === 0}
                          onClick={handleIncrementalPull}
                        >
                          Incremental pull
                          {newCount != null && newCount > 0 ? ` (${newCount} new)` : ''}
                        </Button>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefreshNewCount}>
                          Refresh new count
                        </Button>
                        <Button
                          color="warning"
                          variant="outlined"
                          startIcon={<ResetIcon />}
                          disabled={pulling}
                          onClick={handleResetChannel}
                        >
                          Remove all channel data
                        </Button>
                      </>
                    )}
                  </Stack>

                  {detailLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Published</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell>Visibility</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {allVideos.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                  No videos yet. Run a one-time pull to import metadata.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            allVideos.map((v) => (
                              <TableRow key={v.id} hover>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {v.title}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" component="a" href={v.watch_url} target="_blank" rel="noreferrer">
                                    Watch on YouTube
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {v.published_at ? new Date(v.published_at).toLocaleDateString() : '—'}
                                </TableCell>
                                <TableCell>{formatDuration(v.duration_seconds)}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    icon={v.visibility === 'public' ? <PublicIcon /> : <PrivateIcon />}
                                    label={v.visibility}
                                    color={v.visibility === 'public' ? 'success' : 'default'}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      toggleVisibility(v.id, v.visibility === 'public' ? 'private' : 'public')
                                    }
                                  >
                                    Make {v.visibility === 'public' ? 'private' : 'public'}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      )}

      {subTab === 1 && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingGroupId(null);
                setGroupForm({ name: '', description: '', training_category_id: '' });
                setGroupOpen(true);
              }}
            >
              New course group
            </Button>
          </Stack>
          <Grid container spacing={2}>
            {groups.map((g) => (
              <Grid item xs={12} md={6} key={g.id}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {g.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {g.description || 'No description'}
                        </Typography>
                        <Chip size="small" label={`${g.video_count || 0} videos`} sx={{ mr: 0.5 }} />
                        {g.trainingCategory?.name && (
                          <Chip size="small" color="primary" variant="outlined" label={g.trainingCategory.name} />
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={async () => {
                          if (!window.confirm('Delete this group? Videos stay in the library.')) return;
                          await api.delete(`/training/admin/youtube-library/groups/${g.id}`);
                          toast.success('Group deleted');
                          loadGroups();
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                      <Button size="small" variant="outlined" onClick={() => openAssignVideos(g)}>
                        Assign videos
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setEditingGroupId(g.id);
                          setGroupForm({
                            name: g.name,
                            description: g.description || '',
                            training_category_id: g.training_category_id || '',
                          });
                          setGroupOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Tooltip title="Creates/updates Training Materials for public videos in this group">
                        <Button size="small" variant="contained" startIcon={<SyncIcon />} onClick={() => handleSyncTraining(g.id)}>
                          Sync to training
                        </Button>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add YouTube channel</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Channel URL"
            placeholder="https://www.youtube.com/@freecodecamp"
            value={channelUrl}
            onChange={(e) => setChannelUrl(e.target.value)}
            sx={{ mt: 1 }}
            helperText="Public channel — metadata only, videos are not downloaded"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddChannel}>
            Add channel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={groupOpen} onClose={() => setGroupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroupId ? 'Edit course group' : 'New course group'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Group name"
              fullWidth
              value={groupForm.name}
              onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
              placeholder="Data Engineering"
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={groupForm.description}
              onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Training category (for sync)</InputLabel>
              <Select
                label="Training category (for sync)"
                value={groupForm.training_category_id}
                onChange={(e) => setGroupForm({ ...groupForm, training_category_id: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {categories.filter((c) => !c.is_system_category).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveGroup}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(assignGroupId)} onClose={() => setAssignGroupId(null)} maxWidth="md" fullWidth>
        <DialogTitle>Assign videos to group</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
            Select from all stored library videos:
          </Typography>
          <AssignVideoPicker channels={channels} selectedIds={assignVideoIds} onChange={setAssignVideoIds} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignGroupId(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAssignVideos}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function AssignVideoPicker({ channels, selectedIds, onChange }) {
  const [videosByChannel, setVideosByChannel] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const map = {};
      try {
        for (const ch of channels) {
          const r = await api.get(`/training/admin/youtube-library/channels/${ch.id}`);
          if (!cancelled) map[ch.id] = r.data.data?.videos || [];
        }
      } catch {
        /* ignore */
      }
      if (!cancelled) {
        setVideosByChannel(map);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [channels]);

  if (loading) return <CircularProgress size={24} />;

  const toggle = (id) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter((x) => x !== id));
    else onChange([...selectedIds, id]);
  };

  return (
    <Stack spacing={1}>
      {channels.map((ch) => (
        <Box key={ch.id}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {ch.title}
          </Typography>
          {(videosByChannel[ch.id] || []).map((v) => (
            <Stack key={v.id} direction="row" alignItems="center" spacing={1}>
              <Checkbox checked={selectedIds.includes(v.id)} onChange={() => toggle(v.id)} size="small" />
              <ListItemText primary={v.title} secondary={v.visibility} />
            </Stack>
          ))}
        </Box>
      ))}
    </Stack>
  );
}
